import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { studentAPI } from '../../services/api';

const DISCIPLINES = [
  { value: 'PT', label: 'Physical Therapy (PT)' },
  { value: 'OT', label: 'Occupational Therapy (OT)' },
  { value: 'SP', label: 'Speech (SP)' },
  { value: 'SEIT', label: 'Special Education Itinerant Teacher (SEIT)' },
  { value: 'SW', label: 'Social Work (SW)' },
  { value: 'PSY', label: 'Psychology (PSY)' },
  { value: 'VISION', label: 'Vision Services' },
  { value: 'HEARING', label: 'Hearing Services' },
  { value: 'OTHER', label: 'Other' },
];

const EMPTY_FORM = {
  service_type: 'PT',
  start_date: '',
  end_date: '',
  prescriber: '',
  notes: '',
};

function formatDate(value) {
  if (!value) return '—';
  return String(value).split('T')[0];
}

const TherapyPrescriptionsCard = ({ studentId, canEdit }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!studentId) return;
    setLoading(true);
    studentAPI.getTherapyPrescriptions(studentId)
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      service_type: row.service_type,
      start_date: formatDate(row.start_date) === '—' ? '' : formatDate(row.start_date),
      end_date: row.end_date ? formatDate(row.end_date) : '',
      prescriber: row.prescriber || '',
      notes: row.notes || '',
    });
    setError('');
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.start_date) {
      setError('Start date is the date written on the script.');
      return;
    }
    if (form.end_date && form.end_date < form.start_date) {
      setError('End date is before the start date written on the script.');
      return;
    }
    const payload = {
      service_type: form.service_type,
      start_date: form.start_date,
      end_date: form.end_date || null,
      prescriber: form.prescriber,
      notes: form.notes,
    };
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await studentAPI.updateTherapyPrescription(studentId, editingId, payload);
      } else {
        await studentAPI.createTherapyPrescription(studentId, payload);
      }
      resetForm();
      load();
    } catch (e) {
      const data = e.response?.data;
      setError(data?.end_date?.[0] || data?.detail || data?.start_date?.[0] || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const label = row.discipline_label || row.service_type;
    if (!window.confirm(`Remove the ${label} script dated ${formatDate(row.start_date)}?`)) return;
    try {
      await studentAPI.deleteTherapyPrescription(studentId, row.id);
      if (editingId === row.id) resetForm();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Therapy prescriptions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One script per discipline. Enter the start and end dates printed on the script. Leave the end date blank when the script does not have one.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <Typography variant="body2">Loading scripts…</Typography>}
      {!loading && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No scripts on file.</Typography>
      )}
      {rows.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Discipline</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Prescriber</TableCell>
              <TableCell>Notes</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.discipline_label || row.service_type}</TableCell>
                <TableCell>{formatDate(row.start_date)}</TableCell>
                <TableCell>{row.end_date ? formatDate(row.end_date) : 'No end date'}</TableCell>
                <TableCell>{row.prescriber || '—'}</TableCell>
                <TableCell>{row.notes || '—'}</TableCell>
                {canEdit && (
                  <TableCell>
                    <Button size="small" onClick={() => startEdit(row)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => remove(row)}>Remove</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {canEdit && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {editingId ? 'Edit script' : 'Add script'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Discipline" value={form.service_type} onChange={onChange('service_type')}>
                {DISCIPLINES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={onChange('start_date')} required />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" type="date" label="End date" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={onChange('end_date')} helperText="Blank if the script has none" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" label="Prescriber" value={form.prescriber} onChange={onChange('prescriber')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={onChange('notes')} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={save} disabled={saving}>
                {editingId ? 'Save script' : 'Add script'}
              </Button>
              {editingId && (
                <Button sx={{ ml: 1 }} onClick={resetForm}>Cancel</Button>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default TherapyPrescriptionsCard;
